import { Component, Input, Inject, NgZone, OnDestroy, PLATFORM_ID, ChangeDetectorRef, forwardRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5Local from '@amcharts/amcharts5/locales/es_ES';
import { DashboardBaseChart } from '../shared/dashboard-base-chart';
import { DashboardBaseComponent } from '../shared/dashboard-base-component';
import {ChartLoadingComponent} from "../chart-loading/chart-loading.component";
import {HEX_COLOR_PALETTE} from "../utils/color-palette";

export interface ChartXYSeries {
  /** Data field for Y values */
  field: string;
  /** Legend label */
  name: string;
  /** Hex color for this series */
  color: string;
}

/**
 * Formato WIDE — datos ya pivotados, series explícitas.
 * Úsalo cuando controlas la forma del dataset y cada categoría ya es un campo.
 * Ejemplo de fila: { periodo: 'ENE', corriente: 3.2, vencido: 1.8 }
 */
export interface ChartXYDataWide {
  series: ChartXYSeries[];
  data: any[];
}

/**
 * Formato RAW/LONG — una fila por combinación (seriesField × categoryField × valueField).
 * Los nombres de campo son configurables — no están hardcodeados.
 * El componente pivota automáticamente y crea una serie por cada valor único de seriesField.
 * Úsalo cuando los datos vienen de una API en formato tabular plano.
 *
 * Ejemplo: rows con campos { impuesto: 'ESTAMPILLAS', mes: '012026', monto: 73354000 }
 *   → seriesField: 'impuesto', valueField: 'monto'
 *   → categoryField ya está definido en ChartXYConfig (e.g. 'mes')
 */
export interface ChartXYDataRaw {
  rows: any[];
  /** Campo que agrupa en series (un color/línea por valor único). Ej: 'impuesto', 'categoria', 'tipo' */
  seriesField: string;
  /** Campo con el valor numérico Y. Ej: 'monto', 'valor', 'total' */
  valueField: string;
  /** Colores por valor de seriesField. Si se omite usa la paleta interna. */
  seriesColors?: { [key: string]: string };
}

export interface ChartXYConfig {
  /** Unique DOM id for the amCharts root */
  chartId: string;
  /**
   * Campo del eje X — aplica tanto para dataWide como dataRaw.
   * En dataRaw es el campo que identifica el período/categoría en cada fila. Ej: 'mes', 'periodo'
   */
  categoryField: string;
  /** Show horizontal scrollbar. Defaults to true */
  showScrollbar?: boolean;
  /** Formatter for Y-axis (value) labels and tooltip values */
  yLabelFormatter?: (value: number) => string;
  /** Usa dataWide cuando los datos ya están pivotados y defines las series explícitamente */
  dataWide?: ChartXYDataWide;
  /** Usa dataRaw cuando los datos vienen en formato long (una fila por combinación de serie × período) */
  dataRaw?: ChartXYDataRaw;
}

export interface ChartXYCardConfig {
  title: string;
  sub?: string;
  lastUpdated?: string;
  chart: ChartXYConfig;
}

@Component({
  selector: 'app-chart-xy',
  templateUrl: './chart-xy.component.html',
  styleUrls: ['./chart-xy.component.scss'],
  standalone: true,
  imports: [
    ChartLoadingComponent
  ],
  providers: [
    {
      provide: DashboardBaseComponent,
      useExisting: forwardRef(() => ChartXYComponent)
    }
  ]
})
export class ChartXYComponent extends DashboardBaseChart implements OnDestroy {
  @Input() config!: ChartXYConfig;

  isLoading = true;

  private amChart?: am5xy.XYChart;
  private xAxis?: am5xy.CategoryAxis<am5xy.AxisRenderer>;
  private yAxis?: am5xy.ValueAxis<am5xy.AxisRenderer>;
  private legend?: am5.Legend;
  private seriesList: am5xy.SmoothedXLineSeries[] = [];

  private textColor = am5.color(0xd9e8ff);
  private mutedColor = am5.color(0x9db8d7);

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  protected initChart(): void {
    if (!isPlatformBrowser(this.platformId) || !this.config?.chartId) return;

    this.zone.runOutsideAngular(() => {
      const root = am5.Root.new(this.config.chartId);
      root.setThemes([am5themes_Animated.new(root)]);
      if (root._logo) root._logo.dispose();
      root.locale = am5Local;
      this.root = root;

      const gridColor = am5.color(0x33506f);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: false,
          wheelX: 'panX',
          wheelY: 'zoomX',
          pinchZoomX: true,
          layout: root.verticalLayout,
        })
      );
      this.amChart = chart;

      const cursor = chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none' }));
      cursor.lineY.set('visible', false);

      // X axis
      const xRenderer = am5xy.AxisRendererX.new(root, { minorGridEnabled: true });
      xRenderer.grid.template.set('location', 0.5);
      xRenderer.labels.template.setAll({
        location: 0.5,
        multiLocation: 0.5,
        fill: this.mutedColor,
        fontSize: 10,
      });
      xRenderer.grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.35 });

      this.xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: this.config.categoryField,
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        })
      );

      // Y axis
      const yRenderer = am5xy.AxisRendererY.new(root, {});
      this.yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, { maxPrecision: 0, renderer: yRenderer })
      );
      yRenderer.labels.template.setAll({ fill: this.mutedColor, fontSize: 10 });
      yRenderer.grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.35 });
      if (this.config.yLabelFormatter) {
        const fmt = this.config.yLabelFormatter;
        yRenderer.labels.template.adapters.add('text', (_text, target) => {
          const value = (target.dataItem as any)?.get('value') as number | undefined;
          return value != null ? fmt(value) : _text;
        });
      }

      // Scrollbar
      if (this.config.showScrollbar !== false) {
        chart.set('scrollbarX', am5.Scrollbar.new(root, {
          orientation: 'horizontal',
          marginBottom: 20,
        }));
      }

      // Legend
      this.legend = chart.children.push(
        am5.Legend.new(root, { centerX: am5.p50, x: am5.p50 })
      );
      this.legend.labels.template.setAll({ fill: this.textColor, fontSize: 12 });
      this.legend.valueLabels.template.setAll({ forceHidden: true });

      chart.appear(500, 100);
    });
  }

  load(input: ChartXYDataWide | ChartXYDataRaw): void {
    this.isLoading = false;
    this.cdr.detectChanges();
    if (!this.root) this.initChart();

    if ('rows' in input) {
      this.config.dataRaw = input as ChartXYDataRaw;
    } else {
      this.config.dataWide = input as ChartXYDataWide;
    }
    const { data, series } = this.resolveData();

    // First load — series haven't been created yet
    if (this.seriesList.length === 0 && series.length > 0) {
      this.zone.runOutsideAngular(() => this.createSeries(series, data));
      this.isLoaded = true;
      return;
    }

    this.xAxis?.data.setAll(data);
    this.seriesList.forEach(s => s.data.setAll(data));
    this.isLoaded = true;
  }

  private createSeries(seriesDefs: ChartXYSeries[], data: any[]): void {
    if (!this.xAxis)return;
    this.xAxis.data.setAll(data);

    for (const def of seriesDefs) {
      const color = am5.color(def.color);
      const tooltip = am5.Tooltip.new(this.root, {
        pointerOrientation: 'horizontal',
        labelText: `[bold #fff]{name}[/]\n[#fff]{categoryX}: [#fff]{valueY}`,
      });
      if (this.config.yLabelFormatter) {
        const fmt = this.config.yLabelFormatter;
        tooltip.label.adapters.add('text', (_text, target) => {
          const value = (target.dataItem as any)?.get('valueY') as number | undefined;
          const cat = (target.dataItem as any)?.get('categoryX') as string | undefined;
          return value != null
            ? `[bold #fff]${def.name}[/]\n[#fff]${cat ?? ''}: [#fff]${fmt(value)}`
            : _text;
        });
      }
      if (!this.amChart || !this.yAxis) return;
      const s = this.amChart.series.push(
        am5xy.SmoothedXLineSeries.new(this.root, {
          name: def.name,
          xAxis: this.xAxis,
          yAxis: this.yAxis,
          stroke: color,
          fill: color,
          valueYField: def.field,
          categoryXField: this.config.categoryField,
          legendRangeLabelText: `[#fff]{name}`,
          tooltip,
        })
      );

      s.strokes.template.setAll({strokeWidth: 3});
      s.bullets.push(() =>
        am5.Bullet.new(this.root, {
          sprite: am5.Circle.new(this.root, {radius: 5, fill: color}),
        })
      );
      s.set('setStateOnChildren', true);
      s.states.create('hover', {});
      s.mainContainer.set('setStateOnChildren', true);
      s.mainContainer.states.create('hover', {});
      s.strokes.template.states.create('hover', {strokeWidth: 4});

      s.data.setAll(data);
      s.appear(500);
      this.seriesList.push(s);
    }
    if (!this.amChart) return;
    this.legend?.data.setAll(this.amChart.series.values);
  }

  private resolveData(): { data: any[]; series: ChartXYSeries[] } {
    if (this.config.dataRaw?.rows?.length) {
      return this.pivotLongData();
    }
    return {
      data: this.config.dataWide?.data ?? [],
      series: this.config.dataWide?.series ?? [],
    };
  }

  private pivotLongData(): { data: any[]; series: ChartXYSeries[] } {
    const { rows, seriesField, valueField, seriesColors = {} } = this.config.dataRaw!;
    const catField = this.config.categoryField;
    const map = new Map<string, any>();
    const cats = new Set<string>();

    for (const row of rows) {
      const seriesKey = String(row[seriesField]);
      const periodKey = String(row[catField]);
      cats.add(seriesKey);
      if (!map.has(periodKey)) map.set(periodKey, { [catField]: periodKey });
      map.get(periodKey)![seriesKey] = row[valueField];
    }

    const palette = HEX_COLOR_PALETTE //['#00e5ff', '#00e676', '#ffb020', '#a855f7', '#ff4568', '#2d7dff'];
    const series: ChartXYSeries[] = [...cats].map((cat, i) => ({
      field: cat,
      name: cat,
      color: seriesColors[cat] ?? palette[i % palette.length],
    }));
    const data = [...map.values()];
    return { data, series };
  }

  ngOnDestroy(): void {
    this.root?.dispose();
  }
}
